<template>
  <div class="device-detail">
    <DeviceTeaser :device="device" />
    <div
      v-if="!device.isReachable"
      class="device-detail__not-reachable"
      v-html="t('devices.deviceIsNotReachable', { '@device': device.name })"
    ></div>
    <div class="device-detail__content">
      <TabLinks :tabs="tabs" />
      <component
        role="tabpanel"
        :aria-labelledby="`tab_${activeTab?.index}`"
        :is="activeTab?.component"
        :device="device"
      />
    </div>
  </div>
</template>

<script lang="ts">
  import { DEVICE_TYPE_MYSTROM_WIFI_SWITCH } from '@/constants.ts'
  import Spinner from '@/components/icons/Spinner.vue'
  import TabLinks from '@/components/TabLinks.vue'
  import DeviceTeaser from '@/components/devices/DeviceTeaser.vue'
  import { useStrings } from '@/composables/useStrings.ts'
  import { defineAsyncComponent } from 'vue'
  import { useRoute } from 'vue-router'
  import { useColors } from '@/composables/useColors.ts'
  import { assetPath } from '@/util'
  import { useUnits } from '@/composables/useUnits'

  export default {
    name: 'device-detail',
    props: {
      device: {
        type: Object,
        required: true,
      },
    },
    components: {
      DeviceTeaser,
      Spinner,
      TabLinks,
      DeviceConsumption: defineAsyncComponent(() => import('@/components/devices/DeviceConsumption.vue')),
      DeviceTimer: defineAsyncComponent(() => import('@/components/devices/DeviceTimer.vue')),
      DeviceSettings: defineAsyncComponent(() => import('@/components/devices/DeviceSettings.vue')),
    },
    setup() {
      const currentRoute = useRoute()
      const { t } = useStrings()
      const colors = useColors()
      const colorPrimary = colors.colorPrimary.value;
      const { formatTemperature, formatWattage } = useUnits();

      return {
        t,
        assetPath,
        currentRoute,
        colorPrimary,
        formatTemperature,
        formatWattage
      }
    },
    data() {
      return {
        name: '',
        description: '',
        mode: false,
        isOnChangedLocally: false,
        primaryColor: this.colorPrimary,
        tabs: [
          {
            id: 'consumption',
            index: 0,
            label: this.t('devices.consumption'),
            url: `/devices/efriends/${this.device.id}/consumption`,
            component: 'DeviceConsumption',
            disabled: this.device.deviceType !== DEVICE_TYPE_MYSTROM_WIFI_SWITCH,
            default: this.device.deviceType === DEVICE_TYPE_MYSTROM_WIFI_SWITCH,
          },
          {
            id: 'timer',
            label: this.t('devices.timer'),
            url: `/devices/efriends/${this.device.id}/timer`,
            component: 'DeviceTimer',
            // Note that the device mode means from the backend's point of view.
            // Setting an automated timer is represented in the mode 'manual'.
            // See device manager backend code.
            disabled: this.device.deviceType !== DEVICE_TYPE_MYSTROM_WIFI_SWITCH,
          },
          {
            id: 'settings',
            label: this.t('devices.settings'),
            url: `/devices/efriends/${this.device.id}/settings`,
            component: 'DeviceSettings',
            disabled: false,
            default: this.device.deviceType !== DEVICE_TYPE_MYSTROM_WIFI_SWITCH,
          },
        ],
      }
    },
    beforeMount() {
      this.setValuesFromProp()
    },
    computed: {
      imageUrl() {
        return this.assetPath(
          `device-types/${this.device.deviceType.toLowerCase()}.png`
        )
      },
      consumption() {
        return this.formatWattage(this.device.wattage)
      },
      temperature() {
        return this.formatTemperature(this.device.temperature)
      },
      activeTab() {
        // BUG is hidden here somehow - routerTab is undefined but it's not defaulting
        // the active tab to the default visually.
        return this.tabs
          .filter((tab) => {
            const routerTab = this.currentRoute.params.tab
            return (!routerTab && tab.default) || tab.id === routerTab
          })
          .map((tab, index) => {
            tab.index = index
            return tab
          })[0]
      },
    },
    methods: {
      setValuesFromProp() {
        if (!this.device) {
          return
        }

        this.name = this.device.name
        this.description = this.device.description
        this.mode = this.device.mode
        this.isOnChangedLocally = this.device.isOnChangedLocally
      },
    },
  }
</script>

<style lang="scss">
  .device-detail {
    justify-content: space-between;
    padding-top: $spacer20;
    flex-wrap: wrap;
    margin-bottom: $spacer30;

    @media (min-width: $bp-xs) {
      display: flex;
    }

    @media (min-width: $bp-md) {
      margin-bottom: $spacer50;
    }

    &__name {
      flex-basis: 100%;
      font-size: $font-size-xl;
      margin-bottom: $spacer025;
      display: flex;
      justify-content: space-between;
      align-items: center;
      line-height: $line-height-sm;

      @media (min-width: $bp-sm) {
        font-size: $font-size-huger;
      }
    }

    &__state-unknown {
      .icon {
        width: $spacer20;
        animation: spin 1.8s linear 0s infinite;

        @media (min-width: $bp-sm) {
          width: $spacer30;
        }
      }
    }

    &__general {
      flex-basis: 100%;
      display: flex;
      justify-content: space-between;
      margin-bottom: $spacer10;
      margin-top: $spacer10;
    }

    &__description {
      @media (min-width: $bp-lg) {
        font-size: $font-size-lg;
      }
    }

    &__current {
      text-align: right;
      margin-left: $spacer20;

      &-consumption {
        font-weight: bold;
        font-size: $font-size-lg;
        transition: $transition-base;

        @media (min-width: $bp-md) {
          font-size: $font-size-xl;
        }
      }
    }

    &__img {
      margin-left: auto;
      margin-right: auto;
      max-width: 200px;

      &--not-reachable {
        opacity: 0.5;
      }

      img {
        max-width: 100%;
      }
    }

    &__not-reachable {
      flex-basis: 100%;
      text-align: center;
      margin: $spacer20 0;
      color: $error-color;
      font-size: $font-size-lg;

      em {
        font-style: normal;
        font-weight: $font-weight-bold;
      }
    }

    &__content {
      width: 100%;
      margin-top: $spacer30;
    }

    &__mode {
      select {
        width: 100%;
      }
    }

    &__toggle {
      border: none;
      background: none;
      transition: $transition-base;
      margin-left: $spacer05;
      width: 3rem;

      @media (min-width: $bp-sm) {
        width: 4rem;
      }

      &:hover,
      &:active {
        color: color.adjust($body-color, $lightness: 7%);
      }

      &--pending {
        animation: pending 1.5s ease-in-out 0s infinite;
      }

      &--active {
        color: $green;

        &:hover,
        &:active {
          color: color.adjust($green, $lightness: -5%);
        }
      }

      .icon {
        display: block;
      }
    }
  }
</style>
